import $ from 'jquery';
import h from 'hyperscript';
import merge from 'lodash-es/merge';
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import 'jquery-legacy/plugins/jquery.qtip.css';
import 'jquery-legacy/plugins/jquery.qtip.js';
import mobileHandleIcon from '@disy/cadenza-icons/slider-handle-mobile-15x30.svg';
import handleIcon from '@disy/cadenza-icons/slider-handle-11x24.svg';
import alternativeHandleIcon from '@disy/cadenza-icons/alternative-single-slider-basic.svg';
import alternativeLeftHandleIcon from '@disy/cadenza-icons/alternative-slider-left-basic.svg';
import alternativeRightHandleIcon from '@disy/cadenza-icons/alternative-slider-right-basic.svg';

import { debounce } from 'cadenza/utils/debounce';
import { assert } from 'cadenza/utils/custom-error';
import { icon } from 'cadenza/utils/icon/icon';

import './slider.css';

const MIN_PIXELS_PER_TICK = 4;
const HOVER_TOOLTIP_HIDE_TIMEOUT = 200;

// Visual offset is needed to expand the width of the steps-slider-thumb because the end date
// of the condition value gets subtracted one unit of the smallest condition date format unit.
// See DateTimeCondition._getAdjustedUpperDate()
const VISUAL_OFFSET = 1;
const DEFAULT_STEP_SLIDER_WIDTH = 240;
/**
 * Creates a (noUi-)slider instance.
 *
 * @param {HTMLElement} target - The element to attach the slider to.
 * @param {object} sliderConfig - noUiSlider configuration, see https://refreshless.com/nouislider/slider-options/
 * @param {object} customConfig - Custom config options that get not passed to noUiSlider.
 * @param {boolean} [customConfig.isUnset] - Whether the slider is unset.
 * @param {boolean} [customConfig.simplePips] - Whether to display pips.
 * @param {boolean} [customConfig.alternativeDesign] - Whether to use alternative design (uses different track and handle styling).
 * @param {boolean} [customConfig.steppingMode] - Whether to use stepping mode where the thumb snap to predefined values.
 * @param {Function} [customConfig.handleOverlapSliderInteraction] - Required if steppingMode is true: callback to calculate lower and upper value.
 * @param {Function} [customConfig.formatFunction] - Function to format pips.
 * @return {import('./slider-api').Slider} - (noUi-)slider-API.
 */
export function createSlider (target, sliderConfig,
  {
    isUnset = false,
    simplePips = false,
    alternativeDesign = false,
    steppingMode = false,
    handleOverlapSliderInteraction,
    formatFunction
  } = {}) {
  if (steppingMode) {
    alternativeDesign = true; // Stepping mode uses alternative design but different handles.
    // Use only the first value, calculate the other depending on the time window.
    sliderConfig.start = [ sliderConfig.start[0] ];
    assert(typeof handleOverlapSliderInteraction === 'function',
      'When using steppingMode then handleOverlapSliderInteraction must be passed');
  }
  if (simplePips && sliderConfig.range) {
    const tickCount = (sliderConfig.range.max - sliderConfig.range.min) / sliderConfig.step + 1;
    const sliderWidth = parseInt(getComputedStyle(target.parentElement).width) || 264;
    if (tickCount * MIN_PIXELS_PER_TICK <= sliderWidth) {
      const values = Array.from({ length: tickCount }, (v, k) => k);
      sliderConfig.pips = Object.assign(sliderConfig.pips || {}, {
        mode: 'values',
        values,
        stepped: true,
        density: 100 / tickCount
      });
    } else {
      // always generate pips at the start and end points to display start/end labels, even if tick count is too large.
      sliderConfig.pips = {
        mode: 'positions', // labels only at the given percentage-based positions
        values: [ 0, 100 ], // only at the start and end points
        density: 100 // density 1 pip every 100% -> no pips between start and end
      };
    }
    if (formatFunction) {
      sliderConfig.pips.format = { to: formatFunction };
    }
  }
  if (sliderConfig.keyboardSupport === undefined) {
    sliderConfig.keyboardSupport = false;
  }

  const stepsOffsetElement = h('.slider--steps-offset');
  if (steppingMode) {
    target.appendChild(stepsOffsetElement);
  }
  const slider = noUiSlider.create(target, sliderConfig);
  slider.target.classList.toggle('alternative', alternativeDesign);

  const handles = slider.target.querySelectorAll('.noUi-handle');
  if (steppingMode) {
    slider.target.classList.add('slider--flex-parent');
    slider.target.setAttribute('data-slider-min-value', String(sliderConfig.range.min[0]));
    if (sliderConfig.range.min[0] > 0) {
      // When the slider date range start is after the total date range start, the slider
      // has an offset to the left that is not reachable by dragging the slider thumb.
      stepsOffsetElement.style.flex = String(sliderConfig.range.min[0]);
      slider.target.querySelector('.noUi-base').style.flex =
        (sliderConfig.range.max[0] - sliderConfig.range.min[0]).toString();
    }
    setupSteppingMode(slider, handles, sliderConfig, handleOverlapSliderInteraction);
    slider.target.classList.toggle('range-thumb', true);
  } else if (alternativeDesign) {
    setupAlternativeHandles(handles);
  } else {
    setupHandles(handles);
  }

  if (isUnset) {
    setSliderUnset(slider);
  }

  return slider;
}

function setupHandles (handles) {
  for (const handle of handles) {
    const mobile = icon(mobileHandleIcon, { styleClass: 'mobile-handle-icon' });
    const desktop = icon(handleIcon, { styleClass: 'handle-icon' });
    handle.append(mobile, desktop);
  }
}

function setupAlternativeHandles (handles) {
  const mobile = icon(alternativeHandleIcon, { styleClass: 'mobile-handle-icon' });
  if (handles.length === 2) {
    const desktopLeft = icon(alternativeLeftHandleIcon, { styleClass: 'alternative-handle-icon' });
    const desktopRight = icon(alternativeRightHandleIcon, { styleClass: 'alternative-handle-icon' });
    handles.item(0).append(mobile, desktopLeft);
    handles.item(1).append(mobile, desktopRight);
  } else {
    for (const handle of handles) {
      const desktop = icon(alternativeHandleIcon, { styleClass: 'alternative-handle-icon' });
      handle.append(mobile, desktop);
      handle.classList.add('alternative-handle');
    }
  }

  for (const handle of handles) {
    const addtionalClasses = [ 'alternative-handle' ];
    if (handles.length === 2) {
      addtionalClasses.push('range-handle');
    }
    handle.classList.add(...addtionalClasses);
  }
}

function setupSteppingMode (slider, handles, sliderConfig, handleOverlapSliderInteraction) {
  handles.item(0).append(h('.stepping-handle'));

  const parentWidth = slider.target.getClientRects().item(0)?.width || DEFAULT_STEP_SLIDER_WIDTH;
  const sliderElementWidth = parentWidth * ((sliderConfig.range.max[0] - sliderConfig.range.min[0]) / sliderConfig.range.max[0]);
  const handle = slider.target.querySelector('.noUi-handle-lower');
  const sliderRangeMax = sliderConfig.range.max;

  const initialOverlap =
    handleOverlapSliderInteraction([ sliderConfig.start[0] ], 'slide', slider);
  updateOverlappingHandle(
    slider,
    sliderRangeMax,
    sliderElementWidth,
    handle,
    initialOverlap.upperTickValue - initialOverlap.lowerTickValue,
    sliderConfig);

  const handleSliderInteractionInternal = (values, type) => {
    const { lowerTickValue, upperTickValue } = handleOverlapSliderInteraction(values, type, slider);
    slider.target.dispatchEvent(new CustomEvent(`overlap-slider-${type}`, {
      detail: {
        values: [ lowerTickValue, upperTickValue ]
      }
    }));
    updateOverlappingHandle(
      slider, sliderRangeMax, sliderElementWidth, handle, upperTickValue - lowerTickValue, sliderConfig);
  };

  slider.on('change', (values) => {
    handleSliderInteractionInternal(values, 'change');
  });

  slider.on('slide', (values) => {
    handleSliderInteractionInternal(values, 'slide');
  });
}

// Adjust the width of the slider to be proportional with the time window.
function updateOverlappingHandle (slider, sliderRangeMax, sliderElementWidth, handle, timeWindow, sliderConfig) {
  const left = slider.getPositions()[0] * sliderElementWidth / 100;
  let newHandleWidth = (timeWindow + VISUAL_OFFSET) * sliderElementWidth / (sliderRangeMax - sliderConfig.range.min[0]); // pixel per tick
  if (left + newHandleWidth > sliderElementWidth) {
    newHandleWidth = sliderElementWidth - left;
  }
  handle.style.width = `${newHandleWidth}px`;
  handle.style.right = `-${newHandleWidth}px`;
}

export function setSliderUnset (slider) {
  slider.target.classList.add('unset');
  const removeUnset = () => {
    slider.target.classList.remove('unset');
    // unfortunately slider.off('.unset') does not work because of a bug in noUiSlider.
    slider.off('set.unset');
    slider.off('start.unset');
  };
  slider.on('start.unset', removeUnset);
  slider.on('set.unset', removeUnset);
}

/**
 * Create slider tooltips
 * @param {import('./slider-api').Slider} slider
 * @param {import('./slider-api').SliderTooltipFormatFunction} formatFunction
 * @param {string} separator
 * @param {object} qTipOptions
 */
export function createSliderTooltips (slider, formatFunction, separator, qTipOptions = {}) {
  const initialValues = Array.isArray(slider.options.start) ? slider.options.start : [ slider.options.start ];
  const qTips = initialValues.map((tickValue, handle) => {
    const handleEl = slider.target.querySelector(`.noUi-handle[data-handle="${handle}"]`);
    return createQtip(handleEl, values => formatFunction(values[handle]), qTipOptions);
  });

  const connectElement = slider.target.getElementsByClassName('noUi-connect')[0];
  let connectQTip;
  if (connectElement) {
    const connectFormatFunction = tickValues => tickValues.map(tickValue => formatFunction(tickValue)).join(` ${separator} `) || ' ';
    connectQTip = createQtip(connectElement, connectFormatFunction);
  }
  const tooltipDisplayStarted = Array(initialValues.length).fill(false);
  const KEY_HANDLE = 0;
  let activeQTip;

  const createTooltip = (values, handle) => {
    tooltipDisplayStarted[handle] = true;
    setTimeout(() => {
      if (activeQTip || !tooltipDisplayStarted.some(qTipStarted => qTipStarted)) {
        return;
      }

      if (connectElement && tooltipDisplayStarted.every(qTipStarted => qTipStarted)) {
        // qTip calculates width with jQuery, but jQeury does not take transform: scale into account.
        // To correct tooltip position, we set position.adjust.x parameter to x delta here.
        const adjustX = (connectElement.getBoundingClientRect().width - $(connectElement).outerWidth()) / 2;
        connectQTip.set('position.adjust.x', adjustX);
        activeQTip = connectQTip;
      } else {
        activeQTip = qTips[handle];
      }

      activeQTip.updateValue(values);
      activeQTip.show();
    });
  };

  const updateTooltip = (values) => {
    if (activeQTip) {
      activeQTip.updateValue(values);
    }
  };

  const hideTooltip = (handle) => {
    tooltipDisplayStarted[handle] = false;
    if (activeQTip) {
      activeQTip.hide();
      activeQTip = null;
    }
  };

  slider.on('start', (values, handle) => createTooltip(values, handle));
  slider.on('update', (values) => updateTooltip(values));
  slider.on('end', (values, handle) => hideTooltip(handle));

  const keyUpDebounce = debounce(() => {
    slider.updateOptions({ step: 1 });
    // The slider stops moving anyway when pressing any key other than arrow keys, so we need to hide
    // the tooltip for any key up, otherwise we will have many remaining tooltips displayed.
    hideTooltip(KEY_HANDLE);
  });
  const sliderHandle = slider.target.querySelector('.noUi-handle-lower');
  sliderHandle.onkeydown = (event) => {
    // By default, the slider jumps one unit on an arrow key pressed, when the slider handle is focused.
    // The requirement was that the slider jumps over 10 units when the SHIFT key is hold down
    // and the user presses an arrow key.
    // This is why we are setting the step to 10 when detecting the SHIFT key being down
    // and resetting to the default value of 1 on key up.
    if (event.shiftKey && event.key.startsWith('Arrow')) {
      slider.updateOptions({ step: 10 });
    }
    if ((event.shiftKey || event.key.startsWith('Arrow')) && !tooltipDisplayStarted[KEY_HANDLE]) {
      createTooltip([ slider.get() ], KEY_HANDLE);
    }
  };
  sliderHandle.onkeyup = () => keyUpDebounce();
}

/**
 * Display tooltips when hovering the mouse cursor over the slider.
 * Hover tooltip requires 'hover' behaviour in noUiSlider. To make it work add this to the slider configuration:
 *   { behaviour: 'tap-hover' }
 *
 * @param {import('./slider-api').Slider} slider - the slider
 * @param {import('./slider-api').SliderTooltipFormatFunction} formatFunction - the tooltip format function
 * @param {object} qTipOptions - tooltip options
 * @param {boolean} handleOnly - a flag for setting the tooltip be displayed only when hovering the slider handle.
 * If no value set or set to false then tooltips are displayed when hovering any slider area.
 */
export function createSliderHoverTooltip (slider, formatFunction, qTipOptions = {}, handleOnly = false) {
  let hideTimeout;

  const line = h('.d-slider-hover-line', { hidden: true });
  slider.target.append(line);
  const qTip = createQtip(line, values => formatFunction(values[0]), qTipOptions);

  const showTooltip = (value) => {
    clearTimeout(hideTimeout);

    line.style.left = `${sliderValueToPercent(slider, value)}%`;
    qTip.updateValue([ value ]);

    const qTipVisible = qTip.elements.tooltip && qTip.elements.tooltip.is(':visible');
    if (line.hidden || !qTipVisible) {
      line.hidden = false;
      qTip.show();
    }
  };

  const hideTooltip = () => {
    line.hidden = true;
    qTip.hide();
  };

  if (handleOnly) {
    const handle = slider.target.querySelector('.noUi-handle-lower');
    handle.onmouseenter = () => showTooltip(slider.get());
    handle.onmouseleave = () => hideTooltip();
    handle.onmouseup = () => hideTooltip();
    slider.on('update', () => hideTooltip());
  } else {
    slider.on('hover', value =>  showTooltip(value));

    slider.on('start', () => hideTooltip());
    slider.target.onmouseleave = () => {
      clearTimeout(hideTimeout);
      hideTimeout = setTimeout(hideTooltip, HOVER_TOOLTIP_HIDE_TIMEOUT);
    };
  }
}

function sliderValueToPercent (slider, value) {
  const min = slider.options.range.min;
  const max = slider.options.range.max;
  return Math.round((value - min) / (max - min) * 10000) / 100;
}

function createQtip (target, formatFunction, qTipOptions) {
  if (!target) {
    return undefined;
  }

  const qTip = $(target).qtip(merge({
    show: false,
    hide: false,
    content: ' ',
    style: { classes: 'qtip-light' },
    position: {
      my: 'bottom left',
      at: 'top center',
      effect: false
    }
  }, qTipOptions)).qtip('api');
  qTip.updateValue = values => {
    qTip.set('content.text', formatFunction.call(this, values.map(Math.round)) || ' ');
  };
  return qTip;
}
